import {
  CommonProps,
  IntegrationsVersionOverridesType,
  IntegrationType,
  Plugin,
  PluginManager as IPluginManager,
  PluginType,
} from '@accedo/one-plugin-sdk';
import React, { ComponentType, FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

import { devtoolsApi } from '#/dev/devtools';
import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { filterSentryLogs, logErrorToSentry } from '#/errors/sentry';
import { buildOneErrorContext } from '#/hooks/useDataContext';
import { generateIntegrationAnalyticsEvents } from '#/plugins/analytics/analyticsUtils';
import {
  IntegrationsConfig,
  LegacyAnalyticsEventsConfig,
} from '#/service/config/common.types';
import { RawFeaturesConfig } from '#/service/config/features.types';

import { loadScript, resolveTestIntegration } from './utils/scripts';
import { withCommonProps } from './withCommonProps';

// Both id and src will be deprecated as we move towards client integrations and away from the old plugin object structure.
interface PluginToLoad {
  type?: PluginType;
  id?: string;
  src?: string;
  config: object;
  integrationType?: IntegrationType;
  bundleId?: string;
}

interface GetPluginComponentParams {
  type: IntegrationType;
  id?: string;
  optional?: boolean;
}

type PluginErrorHandler = (error: Error, stack: string) => void;

interface RealPluginComponent<TProps>
  extends React.LazyExoticComponent<React.ComponentType<TProps>> {
  onError?: PluginErrorHandler;
}

interface EmptyPluginComponent<TProps> extends FC<TProps> {
  // the empty component won't have a handler, but this makes the types much cleaner
  onError?: PluginErrorHandler;
}

// unique key for a plugin
const pluginKey = (type?: string, id?: string) => [type, id].join('--');

const emptyPluginKey = '--';

// registry to store loaded plugins
const loadedPlugins: Record<string, Plugin> = {};

// maps for plugin related objects

const pluginPromises: Record<string, Promise<Plugin>> = {};
const pluginConfigs: Record<string, object> = {};

const supportedFeatures = new Set([
  IntegrationType.VIDEO_ADS,
  IntegrationType.PAYWALL,
  IntegrationType.CONSENT,
  IntegrationType.VIDEO_PLAYER,
  IntegrationType.CLIENT_ANALYTICS,
  IntegrationType.PAGE_ADS,
  IntegrationType.VIDEO_PLAYER_OVERLAY,
]);

// This mapping is needed for backwards compatibility while transitioning from plugins to integrations.  When plugins are no longer used in legacy json we should remove this.
const typeMapping: Record<IntegrationType, PluginType> = {
  [IntegrationType.VIDEO_ADS]: PluginType.Ads,
  [IntegrationType.PAYWALL]: PluginType.Paywall,
  [IntegrationType.CONSENT]: PluginType.Privacy,
  [IntegrationType.VIDEO_PLAYER]: PluginType.Player,
  [IntegrationType.CLIENT_ANALYTICS]: PluginType.Analytics,
  [IntegrationType.PAGE_ADS]: PluginType.PageAds,
  [IntegrationType.MONITORING]: PluginType.Monitoring,
  [IntegrationType.VIDEO_PLAYER_OVERLAY]: PluginType.VideoPlayerOverlay,
};

class PluginManager implements IPluginManager {
  // load the given plugins
  loadPlugins(pluginsToLoad: PluginToLoad[]) {
    console.debug('PluginManager - Start loading plugins', pluginsToLoad);

    pluginsToLoad.forEach(
      ({ type, id, src, config = {}, integrationType, bundleId }) => {
        const key = pluginKey(type, id);
        const keyBundleId = pluginKey(integrationType, bundleId);
        const load = loadScript({ src })
          .then(() => {
            const loadedPlugin =
              loadedPlugins[keyBundleId] ?? loadedPlugins[key];

            if (loadedPlugin?.component) {
              loadedPlugin.component.displayName = ['Plugin', type, id].join(
                '-'
              );
            } else {
              console.error(
                `PluginManager - Loaded plugin can't be found. Incorrect id?`,
                {
                  id,
                  type,
                  key,
                  keyBundleId,
                  loadedPlugin: !!loadedPlugin,
                  component: !!loadedPlugin?.component,
                }
              );
            }

            return loadedPlugin;
          })
          .catch((e) => {
            console.warn('PluginManager -', e);
            throw e;
          });

        // While old plugins are used, it's possible they lack integrationType and bundleId, this check is necessary to avoid registering a bunch of legacy plugins on the same key of --
        if (keyBundleId !== emptyPluginKey) {
          pluginConfigs[keyBundleId] = config;
          pluginPromises[keyBundleId] = load;
        }

        pluginConfigs[key] = config;
        pluginPromises[key] = load;
      }
    );
  }

  // promise of all plugin loading promises have either resolved or rejected
  allPluginsLoadedPromise() {
    return Promise.allSettled(Object.values(pluginPromises));
  }

  // Load client integrations and their respective plugins from the integrations config.
  loadIntegrationPlugins(
    integrations: IntegrationsConfig,
    featuresConfig: RawFeaturesConfig,
    analyticsEvents: LegacyAnalyticsEventsConfig,
    integrationsVersionOverrides: IntegrationsVersionOverridesType = {}
  ) {
    const pluginsToLoad: PluginToLoad[] = [];
    const pluginIds: string[] = []; // Backwards compatibility, keep track of the old pluginIds so that we don't double load.

    const promises: ReturnType<typeof resolveTestIntegration>[] = [];

    // filter for supported integrations and resolve any test versions of integrations
    Object.entries(integrations).forEach(([bundleId, integration]) => {
      const hasSupportedFeature = integration.activeForFeatures.find(
        (feature) => supportedFeatures.has(feature)
      );

      if (!hasSupportedFeature) {
        return;
      }

      promises.push(resolveTestIntegration(bundleId, integration));
    });

    return Promise.allSettled(promises)
      .then((results) => {
        results.forEach((result) => {
          if (result.status !== 'fulfilled') {
            return;
          }

          const { bundleId, integration, testUrl } = result.value;
          const { activeForFeatures, configuration } = integration;

          let additionalConfiguration;

          if (activeForFeatures.includes(IntegrationType.CLIENT_ANALYTICS)) {
            // We need to merge analytics events configuration with the integrations.
            additionalConfiguration = generateIntegrationAnalyticsEvents(
              bundleId,
              integration,
              analyticsEvents
            );
          }

          // Take first available supported feature, might want to revisit this in the future
          const hasSupportedFeature = integration.activeForFeatures.find(
            (feature) => supportedFeatures.has(feature)
          );

          if (!hasSupportedFeature) {
            return;
          }

          const type = typeMapping[hasSupportedFeature];
          const override = integrationsVersionOverrides[bundleId];
          const version = override?.version ?? 'latest';

          if (version !== 'latest') {
            console.warn(
              `Integration ${bundleId} has been loaded with specific version ${override.version} with the reason:\n${override.comment}`
            );
          }

          const src =
            testUrl ??
            `/static/plugins/integrations/${bundleId}/web/${version}/index.js`;

          pluginsToLoad.push({
            type,
            id: bundleId,
            src,
            config: {
              ...configuration,
              ...additionalConfiguration,
            },
            integrationType: hasSupportedFeature,
            bundleId,
          });

          pluginIds.push(configuration?.pluginId ?? '');
        });
      })
      .then(() => {
        this.loadPlugins(pluginsToLoad);

        // now that pluginsToLoad is filled, let's load the filtered old plugins
        this.loadFeaturePlugins(featuresConfig, pluginIds);

        return this.allPluginsLoadedPromise();
      });
  }

  //  Old way of loading plugins, only keeping for backwards compatibility, plugins should instead load via integrations configuration
  loadFeaturePlugins(featuresConfig: RawFeaturesConfig, pluginIds: string[]) {
    const pluginsToLoad = Object.values(featuresConfig ?? {})
      .flat()
      .filter((feature) => feature.enabled && feature.plugin)
      .map(({ plugin, properties }) => ({
        type: plugin?.type,
        id: plugin?.id,
        src: plugin?.src,
        pluginId: plugin?.id,
        config: properties,
      }));

    const filteredPlugins = pluginsToLoad.filter(
      ({ id = '' }) => !pluginIds.includes(id)
    );

    this.loadPlugins(filteredPlugins);
  }

  // returns a specific plugin component (withCommonProps, lazy)
  getPluginComponent<TProps>({
    type,
    id,
    optional = false,
  }: GetPluginComponentParams):
    | RealPluginComponent<TProps>
    | EmptyPluginComponent<TProps> {
    const EmptyComponent: FC<TProps> = () => null;
    EmptyComponent.displayName = 'EmptyComponent';

    if (!id) {
      return EmptyComponent;
    }

    const oldType = typeMapping[type] ?? '';

    const bundleKey = pluginKey(type, id);
    const deprecatedPluginKey = pluginKey(oldType, id);

    const integrationPromise = pluginPromises[bundleKey];
    const pluginPromise = pluginPromises[deprecatedPluginKey];

    const promise = integrationPromise ?? pluginPromise;

    // plugin is not found
    if (promise === undefined) {
      // if it's optional, we return an empty component
      if (optional) {
        return EmptyComponent;
      }

      // otherwise, put a rejected promise in the cache
      pluginPromises[bundleKey] = Promise.reject(
        new Error(`Unknown plugin ${JSON.stringify({ type, id })}`)
      );

      // otherwise, put a rejected promise in the cache
      pluginPromises[deprecatedPluginKey] = Promise.reject(
        new Error(`Unknown plugin ${JSON.stringify({ type, id })}`)
      );
    }

    // configs for this plugin
    const pluginConfig =
      pluginConfigs[bundleKey] || pluginConfigs[deprecatedPluginKey] || {};

    // Return a lazy component that waits for the plugin promise and
    // resolves with a module default-exporting the component in the plugin
    const Component: RealPluginComponent<TProps> = React.lazy<
      ComponentType<TProps>
    >(
      () =>
        promise?.then((plugin) => {
          if (!plugin?.component) {
            const method = 'getPluginComponent - falsy component';
            throw new OneError(Errors.WEB_INTEGRATION_GENERAL_UNKNOWN, {
              extras: { bundleKey, deprecatedPluginKey, plugin, method },
            });
          }

          const component = plugin.component as ComponentType<
            PropsWithChildren<TProps & CommonProps>
          >;
          const componentWithConfig = withCommonProps(component, pluginConfig);

          return {
            default: componentWithConfig,
          };
        })
      // we don't catch errors here. we don't have anything to fall back to.
      // we leave that to the error boundary.
    );

    const loadedPlugin =
      loadedPlugins[bundleKey] ?? loadedPlugins[deprecatedPluginKey];

    Component.onError = createErrorHandler({
      plugin: id,
      pluginType: type,
      pluginVersion: loadedPlugin?.version ?? 'unknown',
    });

    return Component;
  }

  getLoadedPlugins() {
    return loadedPlugins;
  }

  // plugins will register themselves with this method
  registerPlugin(plugin: Plugin) {
    const { id, bundleId, type, integrationType, version = 'unknown' } = plugin;
    const keyId = pluginKey(type, id);
    const keyBundleId = pluginKey(integrationType, bundleId);

    // While old plugins are used, it's possible they lack integrationType and bundleId, this check is necessary to avoid registering a bunch of legacy plugins on the same key of --
    if (keyBundleId !== emptyPluginKey) {
      loadedPlugins[keyBundleId] = plugin;
    }

    loadedPlugins[keyId] = plugin;

    const pluginInfo = {
      type,
      config: pluginConfigs[keyBundleId] ?? pluginConfigs[keyId],
      version,
    };

    const integrationInfo = {
      oldPluginId: id,
      config: pluginConfigs[keyBundleId] ?? pluginConfigs[keyId],
      type: integrationType ?? type,
      version,
    };

    if (process.env.goal === 'local') {
      devtoolsApi.setPluginInfo(id, pluginInfo);
      devtoolsApi.setIntegrationInfo(bundleId, integrationInfo);
    }
    console.debug(
      `PluginManager - Registered: ${bundleId ?? id} version ${version}`,
      integrationInfo
    );
  }

  // returns if at least one plugin is configured for the given type, without filtering failed plugins
  isAtLeastOneConfigured(type: IntegrationType): boolean {
    const keyPrefix = pluginKey(type, '');

    return Object.keys(pluginConfigs).some((key) => key.startsWith(keyPrefix));
  }
}

/**
 * Creates an onError handler for plugin/integration component error boundaries to catch unhandled errors and report to Sentry.
 * @param plugin The plugin ID (plugins) or bundle ID (integrations)
 * @param pluginType The plugin/integration type
 * @param pluginVersion The plugin/integration version
 */
const createErrorHandler =
  ({
    plugin,
    pluginType,
    pluginVersion,
  }: {
    plugin: string;
    pluginType: IntegrationType;
    pluginVersion: string;
  }) =>
  (error: Error, stack: string) => {
    const { sendSentryLogs, code, details } = filterSentryLogs(
      error,
      undefined
    );

    if (!sendSentryLogs) {
      return;
    }

    const context = buildOneErrorContext(code, details, {
      plugin,
      pluginType,
      pluginVersion,
    });

    logErrorToSentry(error, context, error.message, stack);
  };

// Create a singleton instance
export const pluginManager = new PluginManager();

// Make the registerPlugin method accessible for plugins
window.pluginManager = {
  registerPlugin: pluginManager.registerPlugin,
};

// Provide externals for the plugins
window.React = React;
window.Styled = { default: styled };
